import React from 'react';
import { Spinner, Alert } from '@c2fo/liquidity';

interface LoadingStateProps {
  message?: string;
  'aria-label'?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Reusable LoadingState component for consistent loading indicators
 * Provides accessible loading states with ARIA labels
 * Uses Liquidity Spinner component
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  'aria-label': ariaLabel,
  size = 'md',
}) => {
  const spinnerSizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div
      className="flex items-center justify-center p-8"
      role="status"
      aria-label={ariaLabel || message}
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-3">
        <Spinner className={spinnerSizeClasses[size]} />
        <span className={`text-gray-600 ${textSizeClasses[size]}`}>{message}</span>
      </div>
    </div>
  );
};

interface ErrorMessageProps {
  message: string;
  onRetry?: () => void;
  'aria-label'?: string;
  className?: string;
}

/**
 * Reusable ErrorMessage component for consistent error display
 * Provides accessible error messages with optional retry functionality
 * Uses Liquidity Alert component
 */
export const ErrorMessage: React.FC<ErrorMessageProps> = ({ message, onRetry, 'aria-label': ariaLabel }) => {
  return (
    <Alert
      type="error"
      variant="standard"
      full
      description={message}
      aria-label={ariaLabel || `Error: ${message}`}
      action={
        onRetry
          ? {
              text: 'Try again',
              onClick: onRetry,
            }
          : undefined
      }
    />
  );
};
